import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Users2, MessageCircle, Flag, StickyNote, Trash2, Pencil, Check, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ThreadView } from "./messages/ThreadView";
import { useFileUploadContext } from "@/lib/fileQueries";
import {
  useCoachMessagesWorkbench, useFlagThreadFollowup,
  useCoachNotes, useCreateCoachNote, useUpdateCoachNote, useDeleteCoachNote,
} from "@/lib/coachWorkbenchQueries";

/**
 * Master v5 Prompt 19 — cross-Workspace Messages workbench: every
 * thread across every Workspace this coach coaches, unread counts,
 * search, and a manual "flag for follow-up" — plus, alongside it, the
 * new CoachNote panel for whichever member/Workspace context is
 * currently selected. A CoachNote here is STRICTLY separate from the
 * thread it sits next to: it never becomes a Message, is never sent,
 * and is never visible to the client on the other end of that thread.
 */
export default function CoachMessagesWorkbenchPage() {
  const [query, setQuery] = useState("");
  const [activeThread, setActiveThread] = useState(null); // { workspaceId, threadId, memberId, memberName, workspaceTitle }
  const { data, isPending, isError } = useCoachMessagesWorkbench(query.trim().length >= 2 ? query.trim() : "");
  const { data: uploadContext } = useFileUploadContext(activeThread?.workspaceId);
  const flagFollowup = useFlagThreadFollowup(activeThread?.workspaceId);
  const [followupReason, setFollowupReason] = useState("");
  const [followupOpen, setFollowupOpen] = useState(false);

  const items = useMemo(() => data?.items || [], [data]);
  useEffect(() => {
    if (!activeThread && items.length > 0) {
      const t = items[0];
      setActiveThread({ workspaceId: t.workspace_id, threadId: t.thread_id, memberId: t.member_id, memberName: t.label, workspaceTitle: t.workspace_title });
    }
  }, [items, activeThread]);

  async function handleFlag() {
    if (!followupReason.trim() || !activeThread) return;
    try {
      await flagFollowup.mutateAsync({ threadId: activeThread.threadId, reason: followupReason.trim() });
      toast.success("Flagged for follow-up — visible on Coach Today");
      setFollowupReason(""); setFollowupOpen(false);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't flag this thread");
    }
  }

  return (
    <div data-testid="coach-messages-workbench-page">
      <h1 className="font-serif text-2xl text-ink mb-1">Messages</h1>
      <p className="text-sm text-ink-soft mb-4">Every thread across your Workspaces, with private notes alongside.</p>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search messages…" className="pl-8" data-testid="coach-messages-workbench-search" />
      </div>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="coach-messages-workbench-loading">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-messages-workbench-error">Couldn't load messages.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-messages-workbench-empty">No threads found.</p>
      ) : (
        <div className="grid md:grid-cols-[220px_1fr_260px] gap-4 border border-ink/10 rounded-lg overflow-hidden bg-paper" data-testid="coach-messages-workbench-layout">
          <div className="border-r border-ink/10 bg-ink/[0.02]" data-testid="coach-messages-workbench-thread-list">
            {items.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => setActiveThread({ workspaceId: t.workspace_id, threadId: t.thread_id, memberId: t.member_id, memberName: t.label, workspaceTitle: t.workspace_title })}
                data-testid={`coach-messages-workbench-thread-${t.thread_id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-ink/5 transition-colors ${activeThread?.threadId === t.thread_id ? "bg-terracotta/10" : "hover:bg-ink/5"}`}
              >
                <div className="flex items-center gap-1.5">
                  {t.visibility_scope === "workspace_shared" ? <Users2 size={13} className="text-terracotta shrink-0" /> : <MessageCircle size={13} className="text-ink-soft shrink-0" />}
                  <span className="text-xs font-medium text-ink truncate flex-1">{t.label}</span>
                  {t.unread_count > 0 && <span className="bg-terracotta text-paper text-[10px] font-semibold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">{t.unread_count}</span>}
                </div>
                <p className="text-[11px] text-ink-soft/70 truncate">{t.workspace_title}</p>
                {t.last_message && <p className="text-[11px] text-ink-soft truncate mt-0.5">{t.last_message.body_text}</p>}
              </button>
            ))}
          </div>

          {activeThread && (
            <div className="min-w-0">
              <div className="flex items-center justify-between px-3 py-2 border-b border-ink/10 bg-ink/[0.015]">
                <p className="text-xs text-ink-soft"><span className="font-medium text-ink">{activeThread.memberName}</span> · {activeThread.workspaceTitle}</p>
                <button onClick={() => setFollowupOpen((v) => !v)} className="text-xs text-ink-soft hover:text-terracotta flex items-center gap-1" data-testid="coach-messages-workbench-flag-btn">
                  <Flag size={12} /> Flag follow-up
                </button>
              </div>
              {followupOpen && (
                <div className="p-2.5 bg-ink/[0.03] flex items-center gap-2" data-testid="coach-messages-workbench-flag-panel">
                  <Input value={followupReason} onChange={(e) => setFollowupReason(e.target.value)} placeholder="Why come back to this?" className="text-xs h-8" data-testid="coach-messages-workbench-flag-input" />
                  <Button size="sm" className="h-8" onClick={handleFlag} disabled={!followupReason.trim() || flagFollowup.isPending} data-testid="coach-messages-workbench-flag-submit">Flag</Button>
                </div>
              )}
              <ThreadView
                workspaceId={activeThread.workspaceId} threadId={activeThread.threadId}
                isTeamWorkspace={uploadContext?.access_type === "team"} actorRole="coach"
                teamMembers={uploadContext?.team_members || []}
                onSentToThread={(threadId) => setActiveThread((prev) => ({ ...prev, threadId }))}
              />
            </div>
          )}

          <CoachNotePanel workspaceId={activeThread?.workspaceId} memberId={activeThread?.memberId} contextLink={activeThread ? { target_type: "message_thread", target_id: activeThread.threadId, label: activeThread.memberName } : null} />
        </div>
      )}
    </div>
  );
}

function CoachNotePanel({ workspaceId, memberId, contextLink }) {
  const { data } = useCoachNotes(memberId);
  const createNote = useCreateCoachNote();
  const updateNote = useUpdateCoachNote();
  const deleteNote = useDeleteCoachNote();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const notes = useMemo(() => (data?.items || []).filter((n) => n.workspace_id === workspaceId), [data, workspaceId]);

  async function handleAdd() {
    if (!draft.trim() || !workspaceId) return;
    await createNote.mutateAsync({ workspace_id: workspaceId, member_id: memberId || null, body_text: draft.trim(), context_link: contextLink });
    setDraft("");
  }

  return (
    <div className="border-l border-ink/10 p-3 flex flex-col min-h-[420px]" data-testid="coach-note-panel">
      <p className="text-xs font-semibold text-ink-soft mb-2 flex items-center gap-1.5"><StickyNote size={13} /> Private notes — never visible to the client</p>
      <div className="flex-1 space-y-2 overflow-y-auto mb-2">
        {notes.length === 0 && <p className="text-xs text-ink-soft italic">No notes yet for this context.</p>}
        {notes.map((n) => (
          <div key={n.cnt_id} className="bg-ink/[0.03] rounded p-2" data-testid={`coach-note-${n.cnt_id}`}>
            {editingId === n.cnt_id ? (
              <div className="space-y-1">
                <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="text-xs" data-testid={`coach-note-edit-input-${n.cnt_id}`} />
                <div className="flex gap-1 justify-end">
                  <button onClick={() => setEditingId(null)} data-testid={`coach-note-edit-cancel-${n.cnt_id}`}><X size={12} className="text-ink-soft" /></button>
                  <button onClick={async () => { await updateNote.mutateAsync({ cntId: n.cnt_id, bodyText: editText }); setEditingId(null); }} data-testid={`coach-note-edit-save-${n.cnt_id}`}><Check size={12} className="text-terracotta" /></button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-ink whitespace-pre-wrap">{n.body_text}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-ink-soft/60">{new Date(n.created_at).toLocaleDateString()}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setEditingId(n.cnt_id); setEditText(n.body_text); }} data-testid={`coach-note-edit-btn-${n.cnt_id}`}><Pencil size={11} className="text-ink-soft hover:text-ink" /></button>
                    <button onClick={() => deleteNote.mutate(n.cnt_id)} data-testid={`coach-note-delete-btn-${n.cnt_id}`}><Trash2 size={11} className="text-ink-soft hover:text-red-600" /></button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Jot a private note about this client…" className="text-xs" data-testid="coach-note-new-input" disabled={!workspaceId} />
      <Button size="sm" className="mt-1.5 self-end" onClick={handleAdd} disabled={!draft.trim() || !workspaceId || createNote.isPending} data-testid="coach-note-add-btn">Add note</Button>
    </div>
  );
}
